import { Box, FormControl, FormHelperText, Grid, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import NumericTextField from "components/NumericTextField";
import SelectProfessionMultiple from "components/SelectProfessionMultiple";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { standardDateFormat } from "utils/dateUtils";
import CreditsSlot, { type CreditLine } from "./CreditsSlot";
import type { ReferenceRow } from "./qualificationLinkState";
import EntryRail from "./EntryRail";

const styles = {
  // Below sm the rail stacks above the form: side by side, its fixed width leaves the form none.
  wrapper: { display: "flex", flexDirection: { xs: "column", sm: "row" }, height: 460 },
  // overflowX hidden: the inner Grid container's negative margin bleeds past pr and
  // triggers a horizontal scrollbar. Poppers render in a portal, so clipping X is safe.
  form: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    pl: { xs: 0, sm: 3 },
    pt: { xs: 2, sm: 0 },
    pr: 1,
  },
} as const;

type Step2Props = {
  entries: ReferenceRow[];
  // Fields an AB-level bulk Edit must not overwrite. Keys match the Field names below;
  // empty means nothing is disabled.
  disabledFields?: Set<string>;
  // The SkuQualification whose stored Subject Area credits are shown, when edit has one.
  skuQualificationId?: string;
  // An edit spanning several SkuQualifications has no single set of credit lines to show.
  creditsVary?: boolean;
  skuCredits?: number;
};

type DatePickerSlotProps = React.ComponentProps<typeof DatePicker>["slotProps"];

const dateSlotProps = (
  error: boolean,
  helperText: React.ReactNode,
): DatePickerSlotProps => ({
  textField: { variant: "standard", error, helperText },
  actionBar: { actions: ["clear", "cancel"] },
});

export default function Step2ConfigureDetails({
  entries,
  disabledFields,
  skuQualificationId,
  creditsVary = false,
  skuCredits,
}: Step2Props) {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const off = (name: string) => disabledFields?.has(name) ?? false;

  // Subject Areas belong to the Accreditation Body, so each accreditor gets its own options.
  // With several accreditors each one gets its own credit lines too: pooling their options let
  // one accreditor's area be sent to another that does not recognize it.
  const accreditorIds = Array.from(
    new Set(entries.map((entry) => entry.accreditationBodyId)),
  );
  const mixedAccreditors = accreditorIds.length > 1;
  const accreditorName = (accreditorId: string) =>
    entries.find((entry) => entry.accreditationBodyId === accreditorId)?.ab;

  const { data: optionsByAccreditor } = useQuery(
    ["skuService.getSubjectAreaOptions", accreditorIds],
    async () => {
      const responses = await Promise.all(
        accreditorIds.map((accreditorId) => skuService.getSubjectAreaOptions(accreditorId)),
      );

      return new Map(accreditorIds.map((id, index) => [id, responses[index].data ?? []]));
    },
    { enabled: accreditorIds.length > 0 },
  );

  const { data: storedCreditLines } = useQuery(
    ["skuService.getSkuQualificationSubjectAreas", skuQualificationId],
    async () => {
      const response = await skuService.getSkuQualificationSubjectAreas(skuQualificationId!);

      return (response.data ?? []).map(
        (split): CreditLine => ({
          key: split.id!,
          id: split.id,
          subjectAreaId: split.subjectAreaId,
          credits: split.credits ?? 0,
        }),
      );
    },
    { enabled: !!skuQualificationId },
  );

  return (
    <Box sx={styles.wrapper}>
      <EntryRail entries={entries} />

      <Box sx={styles.form}>
        <Grid container spacing={2.5}>
          <Field name="professionIds" required>
            {({ input, meta }) => (
              <Grid item xs={12}>
                <FormControl fullWidth error={meta.error && meta.submitFailed}>
                  <SelectProfessionMultiple
                    variant="standard"
                    sentinelsAllowed
                    readOnly={off("professionIds")}
                    value={(input.value as string[]) || []}
                    onChange={(ids) => input.onChange(ids)}
                  />
                  {meta.error && meta.submitFailed && (
                    <FormHelperText>{meta.error}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
          </Field>

          <Field name="courseNumber">
            {({ input, meta }) => (
              <Grid item xs={12} md={6}>
                <TextField
                  variant="standard"
                  fullWidth
                  disabled={off("courseNumber")}
                  label={t({ id: "courseNumber" })}
                  inputProps={{ maxLength: 250 }}
                  InputLabelProps={{ required: false }}
                  error={meta.error && meta.submitFailed}
                  helperText={meta.error && meta.submitFailed && meta.error}
                  {...input}
                />
              </Grid>
            )}
          </Field>

          <Field name="releaseDate" isDate>
            {({ input, meta }) => (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={meta.error && meta.submitFailed}>
                  <DatePicker
                    disabled={off("releaseDate")}
                    label={t({ id: "skuFormReleaseDateLabel" })}
                    format={standardDateFormat}
                    slotProps={dateSlotProps(
                      meta.error && meta.submitFailed,
                      meta.error && meta.submitFailed && meta.error,
                    )}
                    {...input}
                    value={input.value === "" ? null : input.value}
                  />
                </FormControl>
              </Grid>
            )}
          </Field>

          <Field name="creditSubmissionExpirationDate" isDate>
            {({ input, meta }) => (
              <Grid item xs={12}>
                <FormControl fullWidth error={meta.error && meta.submitFailed}>
                  <DatePicker
                    disabled={off("creditSubmissionExpirationDate")}
                    label={t({
                      id: "skuFormCreditSubmissionExpirationDateLabel",
                    })}
                    format={standardDateFormat}
                    slotProps={dateSlotProps(
                      meta.error && meta.submitFailed,
                      meta.error && meta.submitFailed && meta.error,
                    )}
                    {...input}
                    value={input.value === "" ? null : input.value}
                  />
                </FormControl>
              </Grid>
            )}
          </Field>

          <Field name="approvalCode">
            {({ input, meta }) => (
              <Grid item xs={12} md={6}>
                <TextField
                  variant="standard"
                  fullWidth
                  disabled={off("approvalCode")}
                  label={t({ id: "approvalCode" })}
                  InputLabelProps={{ required: false }}
                  error={meta.error && meta.submitFailed}
                  helperText={meta.error && meta.submitFailed && meta.error}
                  {...input}
                />
              </Grid>
            )}
          </Field>

          <Field name="approvalDate" isDate>
            {({ input, meta }) => (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={meta.error && meta.submitFailed}>
                  <DatePicker
                    disabled={off("approvalDate")}
                    label={t({ id: "approvalDate" })}
                    format={standardDateFormat}
                    slotProps={dateSlotProps(
                      meta.error && meta.submitFailed,
                      meta.error && meta.submitFailed && meta.error,
                    )}
                    {...input}
                    value={input.value === "" ? null : input.value}
                  />
                </FormControl>
              </Grid>
            )}
          </Field>

          {creditsVary ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t({ id: "creditLinesVaryByEntry" })}
              </Typography>
            </Grid>
          ) : mixedAccreditors ? (
            <Field name="creditLinesByAccreditor">
              {({ input }) => {
                const byAccreditor = (input.value as Record<string, CreditLine[]> | "") || {};

                return accreditorIds.map((accreditorId) => {
                  const options = optionsByAccreditor?.get(accreditorId);

                  return (
                    // Each block repeats the same Amount and Subject Area controls, so the group
                    // name is what tells a screen reader whose lines they are.
                    <Grid
                      item
                      xs={12}
                      key={accreditorId}
                      role="group"
                      aria-label={accreditorName(accreditorId)}
                    >
                      {options && options.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            { id: "accreditorPublishesNoSubjectAreas" },
                            { accreditor: accreditorName(accreditorId) },
                          )}
                        </Typography>
                      ) : (
                        <CreditsSlot
                          value={byAccreditor[accreditorId] ?? []}
                          onChange={(lines) =>
                            input.onChange({ ...byAccreditor, [accreditorId]: lines })
                          }
                          accreditorLabel={accreditorName(accreditorId)}
                          subjectAreaOptions={options}
                          disabled={off("creditLines")}
                          skuCredits={skuCredits}
                        />
                      )}
                    </Grid>
                  );
                });
              }}
            </Field>
          ) : (
            <Field name="creditLines">
              {({ input }) => (
                <Grid item xs={12}>
                  <CreditsSlot
                    // Untouched the field is "", so the stored splits show through until the
                    // author edits them. Left untouched the save does not rewrite them.
                    value={(input.value as CreditLine[] | "") || storedCreditLines || []}
                    onChange={(lines) => input.onChange(lines)}
                    accreditorLabel={entries[0]?.ab}
                    subjectAreaOptions={optionsByAccreditor?.get(accreditorIds[0])}
                    disabled={off("creditLines")}
                    skuCredits={skuCredits}
                  />
                </Grid>
              )}
            </Field>
          )}

          <Field name="minimumCredit" type="number">
            {({ input, meta }) => (
              <Grid item xs={12} md={6}>
                <NumericTextField
                  variant="standard"
                  fullWidth
                  disabled={off("minimumCredit")}
                  label={t({ id: "minimumCreditMessage" })}
                  error={meta.error && meta.submitFailed}
                  helperText={meta.error && meta.submitFailed}
                  {...input}
                />
              </Grid>
            )}
          </Field>

          <Field name="partialCreditIncrement" type="number">
            {({ input, meta }) => (
              <Grid item xs={12} md={6}>
                <NumericTextField
                  variant="standard"
                  fullWidth
                  disabled={off("partialCreditIncrement")}
                  label={t({ id: "partialCreditIncrementMessage" })}
                  error={meta.error && meta.submitFailed}
                  helperText={meta.error && meta.submitFailed}
                  {...input}
                />
              </Grid>
            )}
          </Field>

          <Field name="notes">
            {({ input, meta }) => (
              <Grid item xs={12} mt={2}>
                <FieldRichText
                  label={t({ id: "notes" })}
                  meta={meta}
                  input={input}
                  disabled={off("notes")}
                />
              </Grid>
            )}
          </Field>
        </Grid>
      </Box>
    </Box>
  );
}
